import React, { useState, useEffect, useMemo } from 'react';
import { Navigate } from 'react-router-dom';
import PageLayout from '../../components/layouts/PageLayout';
import { authService } from '../../auth/authService';
import { canAccessPage } from '../../utils/permissions';
import { getDefaultRoute } from '../../utils/getDefaultRoute';
import { Users, Siren, Clock, Info } from 'lucide-react';
import { ICON_STROKE_WIDTH } from '../../constants';
import { getDashboardStats } from '../../services/dashboardService';
import './AdminDashboard.css';

/**
 * Admin Dashboard (Facility / Centre Level)
 * Data from GET /ris/api/dashboard/stats (RIS backend calls LIMS Registration when configured).
 * Sections: Summary cards, Modality Workload, Financial Summary, TAT.
 */

// Summary card config (order matches API: registrationsToday, emergencyCases, pendingAppointment)
const summaryCardConfig = [
  {
    key: 'registrationsToday' as const,
    title: 'Total Appointments Today',
    change: 'From RIS dashboard',
    icon: Users,
    iconColor: 'var(--admin-card-icon-blue)',
    variant: 'default' as const,
  },
  {
    key: 'emergencyCases' as const,
    title: 'Emergency Cases',
    change: 'Requires immediate attention',
    icon: Siren,
    iconColor: 'var(--admin-card-icon-red)',
    variant: 'warning' as const,
  },
  {
    key: 'pendingAppointment' as const,
    title: 'Pending Appointment',
    change: 'Awaiting completion',
    icon: Clock,
    iconColor: 'var(--admin-card-icon-orange)',
    variant: 'default' as const,
  },
];

const modalityColors = [
  'var(--admin-modality-ct)',
  'var(--admin-modality-mri)',
  'var(--admin-modality-xray)',
  'var(--admin-modality-ultrasound)',
];

/** Format studies as fraction: 0/1 → "0", 1/1 → "1", else "completed/total" */
// TAT – productivity-style cards (title, value, subtitle with colored background)
const tatMetrics = [
  {
    title: 'Avg TAT (CT)',
    value: '2.5h',
    subtitle: 'Goal: < 3h',
    bgColor: 'admin-tat-bg-blue',
    textColor: 'admin-tat-text-blue',
  },
  {
    title: 'Avg TAT (XR)',
    value: '1.8h',
    subtitle: 'Goal: < 2h',
    bgColor: 'admin-tat-bg-cyan',
    textColor: 'admin-tat-text-cyan',
  },
  {
    title: 'Avg TAT (US)',
    value: '1.2h',
    subtitle: 'Goal: < 2h',
    bgColor: 'admin-tat-bg-green',
    textColor: 'admin-tat-text-green',
  },
  {
    title: 'Avg TAT (MR)',
    value: '3.2h',
    subtitle: 'Goal: < 4h',
    bgColor: 'admin-tat-bg-purple',
    textColor: 'admin-tat-text-purple',
  },
];

// Format currency for display (gap between ₹ and amount)
function formatCurrency(amount: number): string {
  if (amount === 0 || Number.isNaN(amount)) {
    return '₹ 0';
  }
  const formatted = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(amount);
  return `₹ ${formatted}`;
}

function DonutChart({
  percentage,
  color,
  label,
  studiesText,
}: Readonly<{ percentage: number; color: string; label: string; studiesText: string }>) {
  const r = 44;
  const circumference = 2 * Math.PI * r;
  const strokeDasharray = (percentage / 100) * circumference;
  return (
    <div className="admin-donut-wrap">
      <div className="admin-donut-visual">
        <svg
          className="admin-donut-svg"
          viewBox="0 0 100 100"
          aria-hidden
        >
          <circle
            className="admin-donut-bg"
            cx="50"
            cy="50"
            r={r}
            fill="none"
            strokeWidth="10"
          />
          <circle
            className="admin-donut-fill"
            cx="50"
            cy="50"
            r={r}
            fill="none"
            strokeWidth="10"
            stroke={color}
            strokeDasharray={`${strokeDasharray} ${circumference}`}
            strokeLinecap="round"
            transform="rotate(-90 50 50)"
          />
        </svg>
        <div
          className="admin-donut-center"
          aria-hidden
        >
          <span className="admin-donut-value">{Math.round(percentage)}%</span>
        </div>
      </div>
      <div className="admin-donut-label">{label}</div>
      <div className="admin-donut-studies">{studiesText}</div>
    </div>
  );
}

/**
 * Financial summary has snapshot amounts only (no time series from API).
 * Sparkline height/level is scaled by value vs max across rows so larger amounts read visually higher.
 */
function FinancialAmountSparkline({
  value,
  maxValue,
  color,
}: {
  value: number;
  maxValue: number;
  color: string;
}) {
  const w = 64;
  const h = 28;
  const pad = 4;
  const n = 9;
  const norm = maxValue > 0 ? Math.min(1, Math.max(0, value / maxValue)) : 0;
  const yTop = pad;
  const yBottom = h - pad;
  const points: number[] = [];
  for (let i = 0; i < n; i++) {
    const t = i / (n - 1);
    const levelAtT = yBottom - norm * (yBottom - yTop) * (0.2 + 0.8 * t);
    const wiggle = Math.sin(t * Math.PI * 1.75) * 2 * Math.max(0.12, norm);
    const y = Math.min(yBottom, Math.max(yTop, levelAtT + wiggle));
    points.push(y);
  }
  const pathD = points.map((y, i) => `${i === 0 ? 'M' : 'L'} ${(i / (n - 1)) * w} ${y}`).join(' ');
  return (
    <svg
      className="admin-sparkline"
      width={w}
      height={h}
      aria-hidden
    >
      <path
        d={pathD}
        fill="none"
        stroke={color}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function AdminDashboard() {
  const [dashboardCounts, setDashboardCounts] = useState<{
    registrationsToday: number | null;
    emergencyCases: number | null;
    pendingAppointment: number | null;
  }>({
    registrationsToday: null,
    emergencyCases: null,
    pendingAppointment: null,
  });
  const [modalityWorkload, setModalityWorkload] = useState<
    Array<{ modalityName: string; completed: number; total: number; displayValue: string }>
  >([]);
  const [dailyRevenue, setDailyRevenue] = useState<number | null>(null);
  const [pendingPayments, setPendingPayments] = useState<number | null>(null);
  const [dashboardLoading, setDashboardLoading] = useState(true);
  const [dashboardError, setDashboardError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setDashboardLoading(true);
    setDashboardError(null);

    const applyDashboardStats = (res: Awaited<ReturnType<typeof getDashboardStats>>) => {
      if (cancelled) {
        return;
      }
      if (!res.success || !res.data) {
        setDashboardError(res.error || 'Failed to load dashboard counts');
        return;
      }
      setDashboardCounts({
        registrationsToday: res.data.registrationsToday,
        emergencyCases: res.data.emergencyCases,
        pendingAppointment: res.data.pendingAppointment,
      });
      if (res.data.modalityWorkload && res.data.modalityWorkload.length > 0) {
        setModalityWorkload(
          res.data.modalityWorkload.map(m => ({
            modalityName: m.modalityName,
            completed: m.completed,
            total: m.total,
            displayValue: m.displayValue ?? (m.total > 0 ? `${m.completed}/${m.total}` : '0/0'),
          }))
        );
      } else {
        setModalityWorkload([]);
      }
      if (res.data.financialSummary) {
        setDailyRevenue(
          typeof res.data.financialSummary.totalCompletedAmount === 'number'
            ? res.data.financialSummary.totalCompletedAmount
            : null
        );
        setPendingPayments(
          typeof res.data.financialSummary.pendingPayments === 'number'
            ? res.data.financialSummary.pendingPayments
            : 0
        );
      }
    };

    getDashboardStats()
      .then(applyDashboardStats)
      .catch(err => {
        if (!cancelled) {
          setDashboardError(err instanceof Error ? err.message : 'Failed to load dashboard counts');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setDashboardLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const summaryCards = summaryCardConfig.map(config => ({
    ...config,
    value:
      dashboardLoading && dashboardCounts[config.key] === null
        ? '—'
        : String(dashboardCounts[config.key] ?? 0),
  }));

  /** Scale financial sparklines together so higher rupee amounts sit higher in the mini chart */
  const financialSparklineMax = useMemo(() => {
    const rev = dailyRevenue ?? 0;
    const pend = pendingPayments ?? 0;
    return Math.max(rev, pend, 1);
  }, [dailyRevenue, pendingPayments]);

  const dashboardUser = authService.getUser();
  if (!canAccessPage(dashboardUser, '/admin-dashboard')) {
    return (
      <Navigate
        to={getDefaultRoute(dashboardUser)}
        replace
      />
    );
  }

  return (
    <PageLayout
      title="Admin Dashboard"
      hideTitleInHeader
      breadcrumbs={[
        { label: 'Home', path: '/' },
        { label: 'Dashboard', path: '/admin-dashboard' },
        { label: 'Admin' },
      ]}
    >
      <div className="admin-dashboard">
        <header className="admin-dashboard-subheader">
          <h1 className="admin-dashboard-subheader-title">Admin Dashboard</h1>
        </header>
        <div className="admin-dashboard-container">
          {/* Summary Cards (Total Registrations Today, Emergency Cases, Pending Appointment from RIS dashboard API) */}
          <section className="admin-section">
            {dashboardError && (
              <p
                className="admin-dashboard-error"
                role="alert"
              >
                {dashboardError}
              </p>
            )}
            <div className="admin-summary-cards">
              {summaryCards.map(metric => {
                const Icon = metric.icon;
                return (
                  <div
                    key={metric.title}
                    className={`admin-summary-card ${metric.variant === 'warning' ? 'admin-summary-card--warning' : ''}`}
                  >
                    <div
                      className="admin-summary-card-icon"
                      style={{ color: metric.iconColor }}
                    >
                      <Icon
                        strokeWidth={ICON_STROKE_WIDTH}
                        size={24}
                      />
                    </div>
                    <h3 className="admin-summary-card-title">{metric.title}</h3>
                    <p className="admin-summary-card-value">{metric.value}</p>
                    <p className="admin-summary-card-change">{metric.change}</p>
                  </div>
                );
              })}
            </div>
          </section>

          {/* Modality Workload + Financial Summary */}
          <div className="admin-two-col">
            <section className="admin-card admin-card--modality">
              <div className="admin-card-title-row">
                <h2 className="admin-card-title admin-card-title--inline">Modality Workload</h2>
                <span className="admin-modality-help-wrap">
                  <button
                    type="button"
                    className="admin-modality-help-btn"
                    aria-describedby="modality-workload-help"
                    aria-label="About modality workload"
                  >
                    <Info
                      strokeWidth={ICON_STROKE_WIDTH}
                      size={18}
                      aria-hidden
                    />
                  </button>
                  <span
                    id="modality-workload-help"
                    className="admin-modality-help-tooltip"
                    role="tooltip"
                  >
                    Each modality shows <strong>completed / total</strong> studies.
                  </span>
                </span>
              </div>
              <div className="admin-modality-grid">
                {modalityWorkload.length === 0 && !dashboardLoading ? (
                  <p className="admin-modality-empty">No modality workload data</p>
                ) : (
                  modalityWorkload.map((m, i) => (
                    <DonutChart
                      key={`${m.modalityName}-${i}`}
                      percentage={m.total > 0 ? (m.completed / m.total) * 100 : 0}
                      color={modalityColors[i % modalityColors.length]}
                      label={m.modalityName}
                      studiesText={`${m.displayValue} studies`}
                    />
                  ))
                )}
              </div>
            </section>

            <section className="admin-card admin-card--financial">
              <h2 className="admin-card-title">Financial Summary</h2>
              <div className="admin-financial-list">
                <div className="admin-financial-row">
                  <div className="admin-financial-text">
                    <span className="admin-financial-label">DAILY REVENUE</span>
                    <span className="admin-financial-value">
                      {dashboardLoading && dailyRevenue === null
                        ? '—'
                        : formatCurrency(dailyRevenue ?? 0)}
                    </span>
                  </div>
                  <FinancialAmountSparkline
                    value={dailyRevenue ?? 0}
                    maxValue={financialSparklineMax}
                    color="var(--admin-sparkline-green)"
                  />
                </div>
                <div className="admin-financial-row">
                  <div className="admin-financial-text">
                    <span className="admin-financial-label">PENDING PAYMENTS</span>
                    <span className="admin-financial-value">
                      {dashboardLoading && pendingPayments === null
                        ? '—'
                        : formatCurrency(pendingPayments ?? 0)}
                    </span>
                  </div>
                  <FinancialAmountSparkline
                    value={pendingPayments ?? 0}
                    maxValue={financialSparklineMax}
                    color="var(--admin-sparkline-orange)"
                  />
                </div>
              </div>
            </section>
          </div>

          {/* TAT – productivity-style cards (gap above from Modality Workload) */}
          <section className="admin-section admin-section-tat">
            <h2 className="admin-section-title">TAT</h2>
            <div className="admin-tat-productivity-grid">
              {tatMetrics.map(metric => (
                <div
                  key={metric.title}
                  className={`admin-tat-productivity-card ${metric.bgColor} ${metric.textColor}`}
                >
                  <p className="admin-tat-productivity-title">{metric.title}</p>
                  <p className="admin-tat-productivity-value">{metric.value}</p>
                  <p className="admin-tat-productivity-subtitle">{metric.subtitle}</p>
                </div>
              ))}
            </div>
          </section>
        </div>
      </div>
    </PageLayout>
  );
}

export default AdminDashboard;
